import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SystemConfigPanel = ({ language, direction }) => {
  const [config, setConfig] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    sessionTimeout: '30',
    passwordMinLength: '8',
    requireSpecialChar: true,
    require2FA: false,
    defaultLanguage: 'fr',
    currencyFormat: 'MAD',
    dateFormat: 'DD/MM/YYYY',
    enableAuditLog: true,
    dataRetentionDays: '365'
  });

  const [isSaving, setIsSaving] = useState(false);

  const labels = {
    title: {
      en: 'System Configuration',
      fr: 'Configuration du système',
      ar: 'تكوين النظام'
    },
    backup: {
      en: 'Backup Settings',
      fr: 'Paramètres de sauvegarde',
      ar: 'إعدادات النسخ الاحتياطي'
    },
    security: {
      en: 'Security Settings',
      fr: 'Paramètres de sécurité',
      ar: 'إعدادات الأمان'
    },
    localization: {
      en: 'Localization Settings',
      fr: 'Paramètres de localisation',
      ar: 'إعدادات التوطين'
    },
    audit: {
      en: 'Audit & Logging',
      fr: 'Audit et journalisation',
      ar: 'التدقيق والتسجيل'
    },
    autoBackup: {
      en: 'Enable Automatic Backup',
      fr: 'Activer la sauvegarde automatique',
      ar: 'تمكين النسخ الاحتياطي التلقائي'
    },
    backupFrequency: {
      en: 'Backup Frequency',
      fr: 'Fréquence de sauvegarde',
      ar: 'تكرار النسخ الاحتياطي'
    },
    sessionTimeout: {
      en: 'Session Timeout (minutes)',
      fr: 'Délai d\'expiration de session (minutes)',
      ar: 'مهلة الجلسة (دقائق)'
    },
    passwordMinLength: {
      en: 'Minimum Password Length',
      fr: 'Longueur minimale du mot de passe',
      ar: 'الحد الأدنى لطول كلمة المرور'
    },
    requireSpecialChar: {
      en: 'Require Special Characters',
      fr: 'Exiger des caractères spéciaux',
      ar: 'يتطلب أحرف خاصة'
    },
    require2FA: {
      en: 'Require Two-Factor Authentication',
      fr: 'Exiger l\'authentification à deux facteurs',
      ar: 'يتطلب المصادقة الثنائية'
    },
    defaultLanguage: {
      en: 'Default System Language',
      fr: 'Langue système par défaut',
      ar: 'لغة النظام الافتراضية'
    },
    currencyFormat: {
      en: 'Currency Format',
      fr: 'Format de devise',
      ar: 'تنسيق العملة'
    },
    dateFormat: {
      en: 'Date Format',
      fr: 'Format de date',
      ar: 'تنسيق التاريخ'
    },
    enableAuditLog: {
      en: 'Enable Audit Logging',
      fr: 'Activer la journalisation d\'audit',
      ar: 'تمكين تسجيل التدقيق'
    },
    dataRetentionDays: {
      en: 'Data Retention Period (days)',
      fr: 'Période de conservation des données (jours)',
      ar: 'فترة الاحتفاظ بالبيانات (أيام)'
    },
    save: {
      en: 'Save Configuration',
      fr: 'Enregistrer la configuration',
      ar: 'حفظ التكوين'
    },
    reset: {
      en: 'Reset to Defaults',
      fr: 'Réinitialiser aux valeurs par défaut',
      ar: 'إعادة تعيين إلى الافتراضي'
    }
  };

  const backupFrequencyOptions = [
    { value: 'hourly', label: language === 'en' ? 'Hourly' : language === 'fr' ? 'Toutes les heures' : 'كل ساعة' },
    { value: 'daily', label: language === 'en' ? 'Daily' : language === 'fr' ? 'Quotidien' : 'يومي' },
    { value: 'weekly', label: language === 'en' ? 'Weekly' : language === 'fr' ? 'Hebdomadaire' : 'أسبوعي' },
    { value: 'monthly', label: language === 'en' ? 'Monthly' : language === 'fr' ? 'Mensuel' : 'شهري' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' }
  ];

  const currencyOptions = [
    { value: 'MAD', label: 'MAD (Moroccan Dirham)' },
    { value: 'EUR', label: 'EUR (Euro)' },
    { value: 'USD', label: 'USD (US Dollar)' }
  ];

  const dateFormatOptions = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
  ];

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      console.log('Configuration saved:', config);
      setIsSaving(false);
    }, 1000);
  };

  const handleReset = () => {
    setConfig({
      autoBackup: true,
      backupFrequency: 'daily',
      sessionTimeout: '30',
      passwordMinLength: '8',
      requireSpecialChar: true,
      require2FA: false,
      defaultLanguage: 'fr',
      currencyFormat: 'MAD',
      dateFormat: 'DD/MM/YYYY',
      enableAuditLog: true,
      dataRetentionDays: '365'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg" style={{ direction }}>
      <div className="px-4 md:px-6 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">{labels?.title?.[language]}</h2>
      </div>
      <div className="p-4 md:p-6 space-y-6">
        <div className="space-y-4">
          <h3 className="text-base font-medium text-foreground flex items-center gap-2">
            <Icon name="Database" size={18} className="text-primary" />
            {labels?.backup?.[language]}
          </h3>
          <div className="space-y-4 pl-0 md:pl-7">
            <Checkbox
              label={labels?.autoBackup?.[language]}
              checked={config?.autoBackup}
              onChange={(e) => handleConfigChange('autoBackup', e?.target?.checked)}
            />
            {config?.autoBackup && (
              <Select
                label={labels?.backupFrequency?.[language]}
                options={backupFrequencyOptions}
                value={config?.backupFrequency}
                onChange={(value) => handleConfigChange('backupFrequency', value)}
              />
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-base font-medium text-foreground flex items-center gap-2">
            <Icon name="Shield" size={18} className="text-primary" />
            {labels?.security?.[language]}
          </h3>
          <div className="space-y-4 pl-0 md:pl-7">
            <Input
              label={labels?.sessionTimeout?.[language]}
              type="number"
              value={config?.sessionTimeout}
              onChange={(e) => handleConfigChange('sessionTimeout', e?.target?.value)}
              min="5"
              max="120"
            />
            <Input
              label={labels?.passwordMinLength?.[language]}
              type="number"
              value={config?.passwordMinLength}
              onChange={(e) => handleConfigChange('passwordMinLength', e?.target?.value)}
              min="6"
              max="20"
            />
            <Checkbox
              label={labels?.requireSpecialChar?.[language]}
              checked={config?.requireSpecialChar}
              onChange={(e) => handleConfigChange('requireSpecialChar', e?.target?.checked)}
            />
            <Checkbox
              label={labels?.require2FA?.[language]}
              checked={config?.require2FA}
              onChange={(e) => handleConfigChange('require2FA', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-base font-medium text-foreground flex items-center gap-2">
            <Icon name="Globe" size={18} className="text-primary" />
            {labels?.localization?.[language]}
          </h3>
          <div className="space-y-4 pl-0 md:pl-7">
            <Select
              label={labels?.defaultLanguage?.[language]}
              options={languageOptions}
              value={config?.defaultLanguage}
              onChange={(value) => handleConfigChange('defaultLanguage', value)}
            />
            <Select
              label={labels?.currencyFormat?.[language]}
              options={currencyOptions}
              value={config?.currencyFormat}
              onChange={(value) => handleConfigChange('currencyFormat', value)}
            />
            <Select
              label={labels?.dateFormat?.[language]}
              options={dateFormatOptions}
              value={config?.dateFormat}
              onChange={(value) => handleConfigChange('dateFormat', value)}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-base font-medium text-foreground flex items-center gap-2">
            <Icon name="FileText" size={18} className="text-primary" />
            {labels?.audit?.[language]}
          </h3>
          <div className="space-y-4 pl-0 md:pl-7">
            <Checkbox
              label={labels?.enableAuditLog?.[language]}
              checked={config?.enableAuditLog}
              onChange={(e) => handleConfigChange('enableAuditLog', e?.target?.checked)}
            />
            {config?.enableAuditLog && (
              <Input
                label={labels?.dataRetentionDays?.[language]}
                type="number"
                value={config?.dataRetentionDays}
                onChange={(e) => handleConfigChange('dataRetentionDays', e?.target?.value)}
                min="30"
                max="3650"
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-end gap-3 px-4 md:px-6 py-4 border-t border-border bg-muted/50">
        <Button
          variant="outline"
          iconName="RotateCcw"
          iconPosition="left"
          onClick={handleReset}
          fullWidth
          className="sm:w-auto"
        >
          {labels?.reset?.[language]}
        </Button>
        <Button
          variant="default"
          iconName="Save"
          iconPosition="left"
          onClick={handleSave}
          loading={isSaving}
          fullWidth
          className="sm:w-auto"
        >
          {labels?.save?.[language]}
        </Button>
      </div>
    </div>
  );
};

export default SystemConfigPanel;